import React, { useState } from 'react';
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Button, 
    Typography, 
    TextField, 
    Box, 
    CircularProgress 
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { RestaurantService } from '../../../api/services/restaurantService';
import { toast } from '../../../utils/toast';

export default function CancelOrderModal({ open, onClose, orderId, onSuccess }) {
    const [cancelReason, setCancelReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {
        if (!cancelReason.trim()) {
            toast.error('Please provide a reason for cancellation.');
            return;
        }

        setIsSubmitting(true);
        try {
            await RestaurantService.cancelRestaurantOrder(orderId, cancelReason);
            toast.success('Restaurant order successfully canceled and charges rolled back from room folio');
            if (onSuccess) onSuccess();
            handleClose();
        } catch (error) {
            console.error('Failed to cancel order:', error);
            // Error handling is managed by the global interceptor
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setCancelReason('');
        setIsSubmitting(false);
        onClose();
    };

    return (
        <Dialog open={open} onClose={isSubmitting ? null : handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', color: 'error.main' }}>
                <WarningAmberIcon sx={{ mr: 1 }} /> Cancel Order / Void Ticket
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body1" fontWeight="bold">
                        Are you sure you want to cancel this order?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        This action will immediately halt kitchen workflow and reverse any pending folio charges. This action cannot be undone.
                    </Typography>
                </Box>
                <TextField
                    autoFocus
                    fullWidth
                    required
                    label="Cancellation Reason"
                    placeholder="e.g., Guest changed mind, Duplicate ticket"
                    multiline
                    rows={3}
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    disabled={isSubmitting}
                />
            </DialogContent>
            <DialogActions sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.02)' }}>
                <Button onClick={handleClose} disabled={isSubmitting} color="inherit">
                    Keep Order
                </Button>
                <Button 
                    onClick={handleSubmit} 
                    color="error" 
                    variant="contained" 
                    disabled={isSubmitting || !cancelReason.trim()}
                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {isSubmitting ? 'Voiding Ticket...' : 'Void Ticket & Cancel Order'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
